// src/pages/Admin/AdminDietaryTagsPage.jsx
import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { ChromePicker } from "react-color";
import api from "../../services/api";

const AdminDietaryTagsPage = () => {
  const [tags, setTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    label: "",
    description: "",
    icon: "",
    color: "4CAF50",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchTags = async () => {
    const res = await api.get("/admin/dietary-tags");
    setTags(res.data);
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleSubmit = async () => {
    try {
      if (editing) {
        await api.put(`/admin/dietary-tags/${editing._id}`, formData);
        setSnackbar({
          open: true,
          message: "Tag updated",
          severity: "success",
        });
      } else {
        await api.post("/admin/dietary-tags", formData);
        setSnackbar({
          open: true,
          message: "Tag created",
          severity: "success",
        });
      }
      setOpen(false);
      setEditing(null);
      setFormData({ label: "", description: "", icon: "", color: "4CAF50" });
      fetchTags();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    await api.delete(`/admin/dietary-tags/${id}`);
    fetchTags();
    setSnackbar({ open: true, message: "Tag deleted", severity: "info" });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Manage Dietary Tags
      </Typography>
      <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
        Add Tag
      </Button>

      <Box sx={{ mt: 3, overflowX: "auto" }}>
        <TableContainer component={Paper} sx={{ minWidth: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                  Label
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                  Description
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                  Icon
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                  Color
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tags.map((tag) => (
                <TableRow key={tag._id}>
                  <TableCell>{tag.label}</TableCell>
                  <TableCell>{tag.description}</TableCell>
                  <TableCell>{tag.icon}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        backgroundColor: `#${tag.color}`,
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={() => {
                        setEditing(tag);
                        setFormData(tag);
                        setOpen(true);
                      }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(tag._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{editing ? "Edit Tag" : "Add Tag"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 1 }}>
            <TextField
              fullWidth
              label="Label"
              value={formData.label}
              onChange={(e) =>
                setFormData({ ...formData, label: e.target.value })
              }
            />
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                border: "1px solid #ccc",
                backgroundColor: `#${formData.color}`,
              }}
            />
          </Box>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            sx={{ my: 1 }}
          />
          <TextField
            fullWidth
            label="Icon (emoji or icon name)"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            sx={{ my: 1 }}
          />
          <Box sx={{ my: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Pick Tag Color
            </Typography>
            <ChromePicker
              color={`#${formData.color}`}
              onChange={(color) =>
                setFormData({ ...formData, color: color.hex.replace(/^#/, "") })
              }
              disableAlpha
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminDietaryTagsPage;
